/**
 * WebSocket client for the notifications API.
 *
 * Owns the connection lifecycle and nothing else: no React, no rendering, no
 * knowledge of what a notification means. NotificationContext drives it.
 *
 * Three behaviours exist because of hard API Gateway limits, not preference:
 *
 *  - **Ping every 5 minutes.** API Gateway closes an idle WebSocket after 10
 *    minutes. The ping is an application-level message so it reaches $default
 *    and unambiguously counts as activity.
 *
 *  - **Proactive reconnect at 1h50m.** API Gateway force-closes *any*
 *    connection at 2 hours. Reconnecting just before that turns an unavoidable
 *    drop into a controlled handover, so the user never sees a gap.
 *
 *  - **Exponential backoff on reconnect.** This is a cost control as much as
 *    good manners: a tight reconnect loop would invoke the $connect Lambda
 *    without limit. Backoff is load-bearing, not polish.
 *
 * The reconnect path is exercised constantly in normal use — browsers throttle
 * background tabs and sleeping laptops drop sockets — so it is the primary
 * path, not an error case.
 */

const PING_INTERVAL_MS = 5 * 60 * 1000;        // 5 min, against a 10 min idle timeout
const RECYCLE_MS = 110 * 60 * 1000;            // 1h50m, against the 2h hard cap
const BACKOFF_MIN_MS = 1000;
const BACKOFF_MAX_MS = 30000;

/**
 * @param {object}   opts
 * @param {string}   opts.url            wss:// endpoint (no query string)
 * @param {function} opts.getToken       async () => Cognito ACCESS token
 * @param {function} opts.onMessage      (payload) => void — one decoded frame
 * @param {function} opts.onOpen         () => void — fires on every (re)connect
 * @param {function} opts.onStateChange  ("connecting"|"connected"|"disconnected") => void
 * @returns {{start: function, stop: function}}
 */
export function createNotificationSocket({ url, getToken, onMessage, onOpen, onStateChange }) {
  let ws = null;
  let pingTimer = null;
  let recycleTimer = null;
  let retryTimer = null;
  let backoff = BACKOFF_MIN_MS;
  let stopped = false;

  const setState = (s) => { try { onStateChange?.(s); } catch { /* listener's problem */ } };

  function clearTimers() {
    [pingTimer, recycleTimer, retryTimer].forEach((t) => t && clearTimeout(t));
    if (pingTimer) clearInterval(pingTimer);
    pingTimer = recycleTimer = retryTimer = null;
  }

  function closeSocket() {
    if (!ws) return;
    // Detach handlers first so our own close doesn't trigger a reconnect.
    ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
    try { ws.close(); } catch { /* already closing */ }
    ws = null;
  }

  function scheduleReconnect() {
    if (stopped) return;
    const delay = backoff;
    backoff = Math.min(backoff * 2, BACKOFF_MAX_MS);
    retryTimer = setTimeout(connect, delay);
  }

  async function connect() {
    if (stopped) return;
    clearTimers();
    closeSocket();
    setState("connecting");

    let token;
    try {
      token = await getToken();
    } catch {
      token = null;
    }
    if (!token) {
      // No session yet, or a refresh failed. Retry rather than giving up — the
      // token usually appears a moment later.
      scheduleReconnect();
      return;
    }

    // The token goes in the query string because a browser cannot set headers
    // on a WebSocket handshake. Encoded so a '+' in a JWT survives.
    const socket = new WebSocket(`${url}?token=${encodeURIComponent(token)}`);
    ws = socket;

    socket.onopen = () => {
      if (ws !== socket) return;          // superseded while connecting
      backoff = BACKOFF_MIN_MS;           // reset only after a real success
      setState("connected");

      pingTimer = setInterval(() => {
        try { socket.send(JSON.stringify({ action: "ping" })); } catch { /* closing */ }
      }, PING_INTERVAL_MS);

      recycleTimer = setTimeout(() => {
        // Pre-empt the 2h cap. Not an error, so no backoff.
        backoff = BACKOFF_MIN_MS;
        connect();
      }, RECYCLE_MS);

      try { onOpen?.(); } catch { /* listener's problem */ }
    };

    socket.onmessage = (evt) => {
      if (ws !== socket) return;
      let payload;
      try {
        payload = JSON.parse(evt.data);
      } catch {
        return;                           // not ours; ignore rather than throw
      }
      if (payload?.type === "notification") {
        try { onMessage?.(payload); } catch { /* listener's problem */ }
      }
    };

    socket.onerror = () => {
      // onclose always follows, so reconnection is handled there.
      if (ws === socket) setState("disconnected");
    };

    socket.onclose = () => {
      if (ws !== socket) return;
      ws = null;
      clearTimers();
      setState("disconnected");
      scheduleReconnect();
    };
  }

  return {
    start() {
      stopped = false;
      backoff = BACKOFF_MIN_MS;
      connect();
    },
    stop() {
      stopped = true;
      clearTimers();
      closeSocket();
      setState("disconnected");
    },
  };
}
